(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-rrr-size-guide]';
  const VARIANT_PICKER_SELECTOR =
    'variant-selects, variant-radios, .product-form__input';

  class RRRSizeGuide {
    constructor(root) {
      this.root = root;

      if (!this.root || this.root.dataset.rrrInitialized === 'true') {
        return;
      }

      this.root.dataset.rrrInitialized = 'true';

      this.mount = this.root.querySelector(
        '[data-rrr-size-guide-mount]'
      );

      this.trigger = this.root.querySelector(
        '[data-rrr-size-guide-open]'
      );

      this.dialog = this.root.querySelector(
        '[data-rrr-size-guide-dialog]'
      );

      this.panel = this.root.querySelector(
        '[data-rrr-size-guide-panel]'
      );

      this.closeButtons = Array.from(
        this.root.querySelectorAll(
          '[data-rrr-size-guide-close]'
        )
      );

      this.unitButtons = Array.from(
        this.root.querySelectorAll(
          '[data-rrr-size-guide-unit]'
        )
      );

      this.measurementCells = Array.from(
        this.root.querySelectorAll(
          '[data-rrr-measurement]'
        )
      );

      this.currentUnitElement = this.root.querySelector(
        '[data-rrr-current-unit]'
      );

      this.unitStatus = this.root.querySelector(
        '[data-rrr-unit-status]'
      );

      this.optionName = this.normaliseText(
        this.root.dataset.optionName || 'Size'
      );

      this.placement =
        this.root.dataset.placement || 'label_right';

      this.baseUnit = this.normaliseUnit(
        this.root.dataset.baseUnit || 'in'
      );

      this.defaultUnit = this.normaliseUnit(
        this.root.dataset.defaultUnit || this.baseUnit
      );

      this.decimals = Number.parseInt(
        this.root.dataset.decimals || '1',
        10
      );

      if (!Number.isFinite(this.decimals)) {
        this.decimals = 1;
      }

      this.decimals = Math.min(
        Math.max(this.decimals, 0),
        2
      );

      this.stripZeros =
        this.root.dataset.stripZeros !== 'false';

      this.isOpen = false;
      this.lastFocusedElement = null;
      this.closeTimer = null;
      this.placementTimer = null;
      this.variantFallbackTimer = null;
      this.domObserver = null;

      /*
       * Mount ki original location save kar rahe hain.
       * Variant picker replace hone par button ko yahin se
       * dobara safely insert kiya jayega.
       */
      this.originalMountParent = this.mount
        ? this.mount.parentNode
        : null;

      this.originalMountNextSibling = this.mount
        ? this.mount.nextSibling
        : null;

      this.target = null;
      this.targetLabel = null;

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.handleTriggerClick =
        this.handleTriggerClick.bind(this);

      this.handleCloseClick =
        this.handleCloseClick.bind(this);

      this.handleUnitClick =
        this.handleUnitClick.bind(this);

      this.handleDocumentKeydown =
        this.handleDocumentKeydown.bind(this);

      this.handleVariantChange =
        this.handleVariantChange.bind(this);

      this.init();
    }

    init() {
      if (
        !this.mount ||
        !this.trigger ||
        !this.dialog ||
        !this.panel
      ) {
        return;
      }

      this.placeTrigger();
      this.bindEvents();
      this.observeVariantPicker();
      this.setUnit(this.defaultUnit, false);

      /*
       * Initial rendering ke baad ek backup placement.
       * Dawn kabhi-kabhi variant picker thoda late render karta hai.
       */
      this.schedulePlacement(100, 5);
    }

    bindEvents() {
      this.trigger.addEventListener(
        'click',
        this.handleTriggerClick
      );

      this.closeButtons.forEach((button) => {
        button.addEventListener(
          'click',
          this.handleCloseClick
        );
      });

      this.unitButtons.forEach((button) => {
        button.addEventListener(
          'click',
          this.handleUnitClick
        );
      });

      document.addEventListener(
        'keydown',
        this.handleDocumentKeydown
      );

      /*
       * Event delegation use ho raha hai, kyunki Dawn variant
       * inputs ko replace kar sakta hai.
       */
      document.addEventListener(
        'change',
        this.handleVariantChange,
        true
      );
    }

    observeVariantPicker() {
      if (!document.body || typeof MutationObserver === 'undefined') {
        return;
      }

      this.domObserver = new MutationObserver((mutations) => {
        if (!this.root.isConnected) {
          return;
        }

        const mountWasRemoved = !this.mount.isConnected;

        const oldTargetWasRemoved =
          this.target && !this.target.isConnected;

        const variantPickerChanged =
          this.mutationsContainVariantPicker(mutations);

        if (
          mountWasRemoved ||
          oldTargetWasRemoved ||
          variantPickerChanged
        ) {
          this.schedulePlacement(0, 6);
        }
      });

      this.domObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    mutationsContainVariantPicker(mutations) {
      return mutations.some((mutation) => {
        const changedNodes = [
          ...mutation.addedNodes,
          ...mutation.removedNodes
        ];

        return changedNodes.some((node) => {
          if (
            node.nodeType !== Node.ELEMENT_NODE &&
            node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE
          ) {
            return false;
          }

          if (
            node.matches &&
            node.matches(VARIANT_PICKER_SELECTOR)
          ) {
            return true;
          }

          return Boolean(
            node.querySelector &&
            node.querySelector(VARIANT_PICKER_SELECTOR)
          );
        });
      });
    }

    handleVariantChange(event) {
      const changedControl =
        event.target && event.target.closest
          ? event.target.closest(
              [
                'variant-selects input',
                'variant-selects select',
                'variant-radios input',
                'variant-radios select',
                '.product-form__input input',
                '.product-form__input select'
              ].join(',')
            )
          : null;

      if (!changedControl) {
        return;
      }

      /*
       * Dawn update start hone se pehle aur complete hone ke baad
       * dono cases cover karne ke liye immediate + fallback check.
       */
      this.schedulePlacement(30, 6);

      window.clearTimeout(this.variantFallbackTimer);

      this.variantFallbackTimer = window.setTimeout(() => {
        this.schedulePlacement(0, 6);
      }, 700);
    }

    schedulePlacement(delay = 0, retries = 5) {
      window.clearTimeout(this.placementTimer);

      const attemptPlacement = (remainingRetries) => {
        if (!this.root.isConnected) {
          return;
        }

        const successfullyPlaced = this.placeTrigger();

        if (!successfullyPlaced && remainingRetries > 0) {
          this.placementTimer = window.setTimeout(() => {
            attemptPlacement(remainingRetries - 1);
          }, 120);
        }
      };

      this.placementTimer = window.setTimeout(() => {
        attemptPlacement(retries);
      }, delay);
    }

    destroy() {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.placementTimer);
      window.clearTimeout(this.variantFallbackTimer);

      if (this.trigger) {
        this.trigger.removeEventListener(
          'click',
          this.handleTriggerClick
        );
      }

      this.closeButtons.forEach((button) => {
        button.removeEventListener(
          'click',
          this.handleCloseClick
        );
      });

      this.unitButtons.forEach((button) => {
        button.removeEventListener(
          'click',
          this.handleUnitClick
        );
      });

      document.removeEventListener(
        'keydown',
        this.handleDocumentKeydown
      );

      document.removeEventListener(
        'change',
        this.handleVariantChange,
        true
      );

      if (this.domObserver) {
        this.domObserver.disconnect();
        this.domObserver = null;
      }

      this.restoreMountToOriginalPosition();
      this.clearCurrentTarget();

      document.documentElement.classList.remove(
        'rrr-size-guide-is-open'
      );

      this.root.dataset.rrrInitialized = 'false';
    }

    normaliseUnit(unit) {
      const value = String(unit || '')
        .trim()
        .toLowerCase();

      return value.startsWith('cm') ? 'cm' : 'in';
    }

    normaliseText(value) {
      return String(value || '')
        .replace(/\s+/g, ' ')
        .replace(/[:*]/g, '')
        .trim()
        .toLowerCase();
    }

    getCleanLabelText(label) {
      if (!label) {
        return '';
      }

      const clone = label.cloneNode(true);

      clone
        .querySelectorAll(
          [
            '.product-form__input__label',
            '[data-selected-value]',
            '.form__label__value',
            'small'
          ].join(',')
        )
        .forEach((element) => element.remove());

      return this.normaliseText(clone.textContent);
    }

    isVisible(element) {
      if (!element || !element.isConnected) {
        return false;
      }

      const styles = window.getComputedStyle(element);

      return (
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        element.getClientRects().length > 0
      );
    }

    findSizeOptionTarget() {
      const preferredScopes = Array.from(
        document.querySelectorAll(
          [
            'product-info',
            '.product__info-container',
            '.featured-product'
          ].join(',')
        )
      ).filter((scope) => this.isVisible(scope));

      const scopes =
        preferredScopes.length > 0
          ? preferredScopes
          : [document];

      for (const scope of scopes) {
        const optionContainers = Array.from(
          scope.querySelectorAll('.product-form__input')
        ).filter((element) => this.isVisible(element));

        for (const container of optionContainers) {
          const label =
            container.querySelector(
              ':scope > legend.form__label'
            ) ||
            container.querySelector(
              ':scope > label.form__label'
            ) ||
            container.querySelector(
              ':scope > .form__label'
            );

          const labelText = this.getCleanLabelText(label);

          if (
            labelText === this.optionName ||
            labelText.startsWith(`${this.optionName} `)
          ) {
            return {
              container,
              label
            };
          }
        }
      }

      return null;
    }

    clearCurrentTarget() {
      if (this.target) {
        this.target.classList.remove(
          'rrr-size-guide-target'
        );
      }

      this.target = null;
      this.targetLabel = null;
    }

    restoreMountToOriginalPosition() {
      if (
        !this.mount ||
        !this.originalMountParent ||
        !this.originalMountParent.isConnected
      ) {
        return;
      }

      if (
        this.originalMountNextSibling &&
        this.originalMountNextSibling.parentNode ===
          this.originalMountParent
      ) {
        this.originalMountParent.insertBefore(
          this.mount,
          this.originalMountNextSibling
        );
      } else {
        this.originalMountParent.appendChild(this.mount);
      }

      this.mount.dataset.placement =
        'section_position';
    }

    placeTrigger() {
      if (
        !this.mount ||
        !this.originalMountParent ||
        !this.root.isConnected
      ) {
        return false;
      }

      if (this.placement === 'section_position') {
        this.clearCurrentTarget();
        this.restoreMountToOriginalPosition();
        return true;
      }

      const targetData = this.findSizeOptionTarget();

      /*
       * Variant picker temporarily missing ho sakta hai.
       * Tab button ko permanently delete hone ke bajay
       * original section position par rakha jayega.
       */
      if (!targetData) {
        this.clearCurrentTarget();
        this.restoreMountToOriginalPosition();
        return false;
      }

      if (
        this.target &&
        this.target !== targetData.container
      ) {
        this.target.classList.remove(
          'rrr-size-guide-target'
        );
      }

      this.target = targetData.container;
      this.targetLabel = targetData.label;

      this.target.classList.add(
        'rrr-size-guide-target'
      );

      this.mount.dataset.placement = this.placement;

      if (this.placement === 'label_right') {
        if (this.mount.parentNode !== this.target) {
          this.target.appendChild(this.mount);
        }

        return true;
      }

      if (
        this.placement === 'above_options' &&
        this.targetLabel
      ) {
        const correctParent =
          this.mount.parentNode === this.target;

        const directlyAfterLabel =
          this.targetLabel.nextElementSibling ===
          this.mount;

        if (!correctParent || !directlyAfterLabel) {
          this.targetLabel.insertAdjacentElement(
            'afterend',
            this.mount
          );
        }

        return true;
      }

      if (this.placement === 'below_options') {
        if (
          this.mount.parentNode !== this.target ||
          this.target.lastElementChild !== this.mount
        ) {
          this.target.appendChild(this.mount);
        }

        return true;
      }

      this.restoreMountToOriginalPosition();
      return true;
    }

    handleTriggerClick(event) {
      event.preventDefault();
      this.open();
    }

    handleCloseClick(event) {
      event.preventDefault();
      this.close();
    }

    handleUnitClick(event) {
      event.preventDefault();

      const unit =
        event.currentTarget.dataset.rrrSizeGuideUnit;

      this.setUnit(unit, true);
    }

    handleDocumentKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    open() {
      if (this.isOpen) {
        return;
      }

      window.clearTimeout(this.closeTimer);

      this.isOpen = true;
      this.lastFocusedElement =
        document.activeElement;

      this.dialog.hidden = false;

      this.trigger.setAttribute(
        'aria-expanded',
        'true'
      );

      document.documentElement.classList.add(
        'rrr-size-guide-is-open'
      );

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.dialog.classList.add('is-open');

          this.panel.focus({
            preventScroll: true
          });
        });
      });
    }

    close() {
      if (!this.isOpen) {
        return;
      }

      this.isOpen = false;

      this.dialog.classList.remove('is-open');

      this.trigger.setAttribute(
        'aria-expanded',
        'false'
      );

      document.documentElement.classList.remove(
        'rrr-size-guide-is-open'
      );

      const delay = this.reducedMotion ? 0 : 320;

      this.closeTimer = window.setTimeout(() => {
        if (!this.isOpen) {
          this.dialog.hidden = true;
        }
      }, delay);

      if (
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus ===
          'function' &&
        this.lastFocusedElement.isConnected
      ) {
        this.lastFocusedElement.focus({
          preventScroll: true
        });
      } else if (this.trigger.isConnected) {
        this.trigger.focus({
          preventScroll: true
        });
      }
    }

    trapFocus(event) {
      const focusableSelectors = [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      const focusableElements = Array.from(
        this.panel.querySelectorAll(
          focusableSelectors
        )
      ).filter((element) => this.isVisible(element));

      if (focusableElements.length === 0) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const firstElement = focusableElements[0];

      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    setUnit(unit, announce = true) {
      const selectedUnit =
        this.normaliseUnit(unit);

      this.currentUnit = selectedUnit;

      this.measurementCells.forEach((cell) => {
        const baseValue =
          cell.dataset.baseValue ?? '';

        cell.textContent = this.convertValue(
          baseValue,
          this.baseUnit,
          selectedUnit
        );
      });

      this.unitButtons.forEach((button) => {
        const buttonUnit = this.normaliseUnit(
          button.dataset.rrrSizeGuideUnit
        );

        const isActive =
          buttonUnit === selectedUnit;

        button.classList.toggle(
          'is-active',
          isActive
        );

        button.setAttribute(
          'aria-pressed',
          String(isActive)
        );
      });

      const unitLabel =
        selectedUnit === 'cm'
          ? 'CM'
          : 'INCHES';

      if (this.currentUnitElement) {
        this.currentUnitElement.textContent =
          unitLabel;
      }

      if (announce && this.unitStatus) {
        this.unitStatus.textContent =
          `Measurements changed to ${unitLabel}`;
      }
    }

    convertValue(value, fromUnit, toUnit) {
      const originalValue = String(value || '');

      if (
        !originalValue ||
        fromUnit === toUnit
      ) {
        return originalValue;
      }

      const conversionFactor =
        fromUnit === 'in' && toUnit === 'cm'
          ? 2.54
          : 1 / 2.54;

      return originalValue.replace(
        /\d+(?:\.\d+)?/g,
        (numberString) => {
          const number = Number.parseFloat(
            numberString
          );

          if (!Number.isFinite(number)) {
            return numberString;
          }

          const converted =
            number * conversionFactor;

          return this.formatNumber(converted);
        }
      );
    }

    formatNumber(number) {
      let formatted = number.toFixed(
        this.decimals
      );

      if (this.stripZeros) {
        formatted = formatted
          .replace(/(\.\d*?[1-9])0+$/, '$1')
          .replace(/\.0+$/, '');
      }

      return formatted;
    }
  }

  function initialiseSizeGuides(scope = document) {
    const roots = [];

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll(ROOT_SELECTOR)
      );
    }

    roots.forEach((root) => {
      if (root.dataset.rrrInitialized !== 'true') {
        root.rrrSizeGuideInstance =
          new RRRSizeGuide(root);
      }
    });
  }

  function initialiseDocument() {
    initialiseSizeGuides(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseDocument,
      { once: true }
    );
  } else {
    initialiseDocument();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseSizeGuides(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const roots = [];

      if (
        event.target.matches &&
        event.target.matches(ROOT_SELECTOR)
      ) {
        roots.push(event.target);
      }

      if (event.target.querySelectorAll) {
        roots.push(
          ...event.target.querySelectorAll(
            ROOT_SELECTOR
          )
        );
      }

      roots.forEach((root) => {
        if (
          root.rrrSizeGuideInstance &&
          typeof root.rrrSizeGuideInstance.destroy ===
            'function'
        ) {
          root.rrrSizeGuideInstance.destroy();
        }
      });
    }
  );
})();